import config from '../../../config/ticker-search.json';

export type TickerItem={symbol:string;name?:string};
export type AutocompleteState={query:string;items:TickerItem[];loading:boolean;error:string};
type FetchItems=(query:string,signal:AbortSignal)=>Promise<unknown>;
export function normalizeAutocompleteItems(raw:unknown):TickerItem[] {
  if(!Array.isArray(raw))return [];
  return raw.flatMap(row=>{
    if(!row||typeof row!=='object')return [];
    const symbol=typeof row.symbol==='string'?row.symbol:typeof row.ticker==='string'?row.ticker:'';
    return symbol?[{symbol,name:typeof row.name==='string'?row.name:undefined}]:[];
  }).slice(0,8);
}

export function createTickerAutocomplete(fetchItems:FetchItems,emit:(state:AutocompleteState)=>void) {
  const cache=new Map<string,{items:TickerItem[];at:number}>();
  let revision=0,timer:ReturnType<typeof setTimeout>|undefined,controller:AbortController|undefined;
  let pending:{query:string;promise:Promise<TickerItem[]>}|undefined;
  let items:TickerItem[]=[];
  const cached=(query:string)=>{
    const hit=cache.get(query);
    return hit&&Date.now()-hit.at<config.clientCacheTtlMs?hit.items:undefined;
  };
  const cancel=()=>{revision++;clearTimeout(timer);controller?.abort();pending=undefined;};
  const load=(query:string,id:number)=>{
    controller=new AbortController();
    const promise=fetchItems(query,controller.signal).then(raw=>{
      const result=normalizeAutocompleteItems(raw);
      if(id!==revision)return result;
      cache.delete(query);cache.set(query,{items:result,at:Date.now()});
      if(cache.size>config.clientCacheSize)cache.delete(cache.keys().next().value!);
      items=result;emit({query,items,loading:false,error:''});return result;
    }).catch(()=>{
      if(id===revision){items=[];emit({query,items,loading:false,error:'검색 결과를 불러오지 못했습니다. 다시 입력해 주세요.'});}
      return [];
    }).finally(()=>{if(id===revision)pending=undefined;});
    pending={query,promise};return promise;
  };
  const search=(raw:string)=>{
    cancel();const query=raw.trim(),hit=cached(query);
    if(!query||hit){items=hit||[];emit({query,items,loading:false,error:''});return;}
    emit({query,items,loading:true,error:''});
    const id=revision;timer=setTimeout(()=>{void load(query,id);},config.debounceMs);
  };
  const resolve=(raw:string)=>{
    const query=raw.trim(),hit=cached(query);
    if(hit)return Promise.resolve(hit);
    if(pending?.query===query)return pending.promise;
    cancel();return query?load(query,revision):Promise.resolve([]);
  };
  return {search,resolve,dispose:cancel};
}
